import React, { useEffect, useMemo, useState } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useMatch,
  useNavigate,
} from "react-router-dom";
import Dashboard from './pages/Dashboard';
import Containers from './pages/Containers';
import Tasks from './pages/Tasks';
import { Box, CssBaseline, AppBar, Toolbar, Typography, IconButton, useMediaQuery, GlobalStyles, Snackbar } from '@mui/material';
import { Home, Menu } from '@mui/icons-material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ScienceIcon from '@mui/icons-material/Science';
import CoronavirusIcon from '@mui/icons-material/Coronavirus';
import TaskIcon from '@mui/icons-material/Task';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { LicenseInfo } from '@mui/x-license';
import { muiLicenseKey } from 'utils/utils';
import CellCulture from './pages/samples/CellCulture';
import CryoBank from './pages/samples/CryoBank';
import Plasmids from './pages/samples/Plasmids';
import DNA from './pages/samples/DNA';
import Oligo from './pages/samples/Oligo';
import Bacteria from './pages/samples/Bacteria';
import GlycerolStock from './pages/samples/GlycerolStock';
import Search from './components/Search';
import Lentivirus from './pages/samples/Lentivirus';
import Generic from './pages/samples/Generic';
import Chemical from './pages/samples/Chemical';
import ObjectSubtypesView from 'layouts/ObjectSubtypesView';
import PCR from 'pages/tasks/PCR';
import Assembly from 'pages/tasks/Assembly';
import Sanger from 'pages/tasks/Sanger';
import Digest from 'pages/tasks/Digest';
import Login from 'pages/Login';
import axios from 'axios';
import CellPellet from 'pages/samples/CellPellet';
import Anneal from 'pages/tasks/Anneal';
import Inventory from 'pages/tasks/Inventory';
import Settings from 'pages/Settings';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Kron from 'pages/tasks/Kron';
import AIChat from './pages/AIChat';

export default function App() {
  const [message, setMessage] = useState(null)
  // TODO: buy MUI-X license before production
  LicenseInfo.setLicenseKey(muiLicenseKey);
  const match = useMatch(":main?/:sub?/*")
  useEffect(() => {
    if (match?.params?.main === "samples") {
      document.title = "Samples | Frab";
    }
    else if (match?.params?.main === "containers") {
      document.title = "Containers | Frab";
    }
    else if (match?.params?.main === "tasks") {
      document.title = "Tasks | Frab";
    }
    else if (match?.params?.main === "login") {
      document.title = "Login | Frab";
    }
    else if (match?.params?.main === "settings") {
      document.title = "Settings | Frab";
    }
    else if (match?.params?.main === "ai") {
      document.title = "AI Assistant | Frab";
    }
    else if (match?.params?.main === undefined) {
      document.title = "Dashboard | Frab"
    }
    else {
      document.title = "Frab"
    }
  }, [match]);
  const isPrintMedia = useMediaQuery('print')
  const theme = useMemo(() =>
      createTheme({
        palette: { mode: isPrintMedia ? 'light' : 'dark' },
        typography: {
          fontSize: isPrintMedia ? 11 : undefined,
        },
        spacing: isPrintMedia ? 2 : undefined,
        components: {
          MuiTableCell: {
            styleOverrides: {
              root: {
                padding: isPrintMedia ? 2 : undefined,
              },
            },
          },
          MuiStepConnector: {
            styleOverrides: {
              lineVertical: {
                minHeight: isPrintMedia ? 4 : undefined,
              },
            },
          },
        }
      }),
    [isPrintMedia],
  )
  const navigate = useNavigate()
  const [drawerOpen, setDrawerOpen] = useState(true)
  const [isAdmin, setIsAdmin] = useState(null)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  useEffect(() => {
    axios.get('/api/settings/user')
      .then(({data}) => setIsAdmin(data.is_admin))
      .catch(error => {
        if (error.response?.status !== 401)
          console.error(error)
      })
  }, [])
  axios.interceptors.response.use(
    response => response,
    error => {
      if (error.response.status === 401) {
        navigate("/login")
      }
      return Promise.reject(error)
    }
  )
  useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false)
    }
  }, [isMobile])
  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Box sx={{ pt: ["login"].includes(match?.params?.main) ? 0 : 8, position: "fixed", height: "100%", width: "100%" }}>
          <CssBaseline enableColorScheme /> {/* enableColorScheme so that scrollbars look right in dark mode */}
          <GlobalStyles styles={{
            '@media print': {
              'div.hideOnPrint': {
                display: 'none',
              },
            },
          }} />
            <Routes>
              <Route path="login" element={<Login />} />
              <Route path="inventory" element={<Navigate to="/tasks/inventory" />} />
              <Route path="kron" element={isAdmin ? <Navigate to="/tasks/kron" /> : <Navigate to="/tasks" />} />
              <Route path="*" element={
                <>
                  <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                    <Toolbar>
                      <Routes>
                        <Route path="samples/:type" element={
                          isMobile && <IconButton color="inherit" onClick={() => setDrawerOpen(!drawerOpen)} aria-label="open navigation menu">
                            <Menu />
                          </IconButton>
                        }>
                        </Route>
                        <Route path="tasks/:type" element={
                          isMobile && <IconButton color="inherit" onClick={() => setDrawerOpen(!drawerOpen)} aria-label="open navigation menu">
                            <Menu />
                          </IconButton>
                        }>
                        </Route>
                        <Route path="ai" element={
                          isMobile && <IconButton color="inherit" onClick={() => setDrawerOpen(!drawerOpen)} aria-label="open previous chats">
                            <Menu />
                          </IconButton>
                        }>
                        </Route>
                      </Routes>
                      <Typography
                        component="h1"
                        variant="h6"
                        color="inherit"
                        noWrap
                        sx={{ flexGrow: 1 }}
                      >
                        <Routes>
                          <Route path="/" element="Dashboard" />
                          <Route path="/containers" element="Containers" />
                          <Route path="samples/*" element="Samples"/>
                          <Route path="tasks">
                            <Route path="inventory/*" element="Frab Inventory"/>
                            {isAdmin && <Route path="kron/*" element="Kron"/>}
                            <Route path="*" element="Tasks"/>
                          </Route>
                          <Route path="settings" element="Settings" />
                          <Route path="ai" element="AI Assistant" />
                        </Routes>
                      </Typography>
                      {(match?.params?.sub !== "inventory") && <>
                        <Search />
                        <IconButton color="inherit" onClick={() => navigate("/")} aria-label="dashboard">
                          <Home />
                        </IconButton>
                        <IconButton
                          color="inherit"
                          onClick={() => {
                            if (match?.params?.main === "ai")
                              setDrawerOpen(!drawerOpen)
                            else {
                              navigate("/ai")
                              setDrawerOpen(true)
                            }
                          }}
                          aria-label="AI chat"
                        >
                          <AutoAwesomeIcon />
                        </IconButton>
                        <IconButton
                          color="inherit"
                          onClick={() => {
                            if (match?.params?.main === "samples")
                              setDrawerOpen(!drawerOpen)
                            else {
                              navigate("/samples")
                              setDrawerOpen(true)
                            }
                          }}
                          aria-label="samples"
                        >
                          <CoronavirusIcon />
                        </IconButton>
                        <IconButton color="inherit" onClick={() => navigate("/containers")} aria-label="containers">
                          <ScienceIcon />
                        </IconButton>
                        <IconButton
                          color="inherit"
                          onClick={() => {
                            if (match?.params?.main === "tasks")
                              setDrawerOpen(!drawerOpen)
                            else {
                              navigate("/tasks")
                              setDrawerOpen(true)
                            }
                          }}
                          aria-label="tasks"
                        >
                          <TaskIcon />
                        </IconButton>
                        <IconButton color="inherit" onClick={() => navigate("/settings")} aria-label="settings">
                          <SettingsIcon />
                        </IconButton>
                      </>}
                    </Toolbar>
                  </AppBar>
                  <Box sx={{height: "100%", overflowY: "auto"}}>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="samples" element={
                        <ObjectSubtypesView
                          views={{
                            "Mammalian": {"tc": "Cell Culture", "cryobank": "Cryobank", "pellet": "Cell Pellet", "lentivirus": "Lentivirus"},
                            "Nucleic Acid": {"plasmids": "Plasmids", "dna": "DNA", "oligo": "Oligos"},
                            "Bacterial": {"bacteria": "Bacteria", "glycerolstock": "Glycerol Stock"},
                            "Miscellaneous": {"chemical": "Chemical", "generic": "Generic"},
                          }}
                          matchUrl="samples/:type"
                          mobileOpen={drawerOpen}
                          setMobileOpen={setDrawerOpen}
                        />}
                      >
                        <Route path="tc" element={<CellCulture showMessage={setMessage} />} />
                        <Route path="cryobank" element={<CryoBank />} />
                        <Route path="pellet" element={<CellPellet />} />
                        <Route path="lentivirus" element={<Lentivirus />} />
                        <Route path="plasmids" element={<Plasmids />} />
                        <Route path="generic" element={<Generic />} />
                        <Route path="chemical" element={<Chemical />} />
                        <Route path="dna" element={<DNA />} />
                        <Route path="oligo" element={<Oligo />} />
                        <Route path="bacteria" element={<Bacteria />} />
                        <Route path="glycerolstock" element={<GlycerolStock />} />
                        <Route path="" element={<Navigate to="tc" />} />
                      </Route>
                      <Route path="containers" element={<Containers />} />
                      <Route path="tasks" element={
                        (match?.params?.sub !== "inventory") &&
                        <ObjectSubtypesView
                          matchUrl="tasks/:type"
                          views={{
                            "": {"generic": "Generic", "pcr": "PCR", "assembly": "Assembly", "sanger": "Sanger", "digest": "Digest", "anneal": "Anneal", "inventory": "Inventory", ...(isAdmin && {"kron": "Kron"})},
                          }}
                          mobileOpen={drawerOpen}
                          setMobileOpen={setDrawerOpen}
                        />}
                      >
                        <Route path="generic" element={<Tasks />} />
                        <Route path="pcr" element={<PCR />} />
                        <Route path="assembly" element={<Assembly />} />
                        <Route path="sanger" element={<Sanger />} />
                        <Route path="digest" element={<Digest />} />
                        <Route path="anneal" element={<Anneal />} />
                        <Route path="inventory/*" element={<Inventory />} />
                        {isAdmin && <Route path="kron" element={<Kron />} />}
                        <Route path="" element={<Navigate to="generic" />} />
                      </Route>
                      <Route path="settings" element={<Settings isAdmin={isAdmin} />} />
                      <Route path="ai" element={
                        <AIChat
                          drawerOpen={drawerOpen}
                          setDrawerOpen={setDrawerOpen}
                        />
                      } />
                    </Routes>
                  </Box>
                </>
              } />
            </Routes>
            <Snackbar open={!!message} autoHideDuration={6000} onClose={() => setMessage(null)} message={message} />
        </Box>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
